import Konva from 'konva';
import HaspObject from './HaspObject';
import CustomArc from '../draw/CustomArc';
import CustomRect from '../draw/CustomRect';
import { processIconText } from '../utils/iconFonts';
import { FontBinding } from '../services/FontBinding.js';
import { useFontStore } from '../stores/fontStore.js';
import { resolvePadding, resolveKnobPadding, onPaddingStyle } from './properties/v2/padding.js';
import { normalizeRangeValues } from './barGeometry.js';
import { executeActions } from '../actions/previewActionDispatcher.js';
import {
    lvglArcGeometry,
    lvglArcKnobAnchor,
    lvglArcKnobBounds,
    lvglArcValueFromPoint,
    lvglArcValueAngles,
    arcKnobCount,
} from './arcGeometry.js';
import { createContentClipGroup } from './contentClip.js';
import { attachImageSourcePreview } from './imageSource.js';

const ARC_RANGE_TYPE = 3;
let arcRangeInteractionId = 0;

// min	        int16	0	minimum value of the indicator
// max	        int16	100	maximum value of the indicator
// val	        int16	0	current value of the indicator
// rotation	    int16	0	offset to the 0 degree position
// type	        0-3	0	0 = normal, 1 = symmetrical, 2 = reverse, 3 = range
// adjustable	bool	false	Add knob that the user can operate to change the value
// start_angle	0-360	start angle of the arc background (see note)
// end_angle	0-360	end angle of the arc background (see note)
// the firmware also supports separate start/end angles for the indicator arc
// (not exposed by this schema/widget yet)

//when the arc height is larger than the width the arc is always drawn in top

// Zero degree is at the middle right (3 o'clock) of the object and the degrees are increasing in a clockwise direction.
// The angles should be in the [0-360] range.
// min, max and val also support negative values.

// To adjust the size of the knob, use the knob part's padding styling property.
// If you increase the knob beyond the margins of the object, you also need to increase pad_top, pad_bottom, pad_left, pad_right for the arc itself.
// Check out value styling to display a textual value in the middle of the arc.

// Reference migration to the StyleResolver API (see v2/StyleResolver.js,
// ObjectGroup.style/setStyle/pokeStyle/onStyle). Widget DATA (min, max, val,
// type, rotation, start_angle, end_angle, adjustable, value_str/font/color)
// stays on the legacy this.on(...)/adjust() mechanism unchanged — only STYLE
// properties (schema.parts.{main,indicator,knob}) were migrated.

export default class HaspArc extends HaspObject {
    hasp_type_options = [
        { value: 0, description: "normal" },
        { value: 1, description: "symmetrical" },
        { value: 2, description: "reverse" },
        { value: 3, description: "range" },
    ]

    constructor(config) {
        super(config);
        this.normalizeDataValues();
        // console.log('HaspArc constructor', config);
        //background arc
        this.hasp_angle = 360 + config.end_angle - config.start_angle
        if (this.hasp_angle > 360) this.hasp_angle = this.hasp_angle - 360;

        this._valueFontBinding = new FontBinding((family) => {
            this.valueText.fontFamily(family);
            this.valueText.getLayer()?.batchDraw();
        });

        this.on('transform', function () {
            if (this.scaleX() === 1) {
                this.fixLinewidth();
                this.updateObjectSize();
                this.updateValueArc();
            }
        });

        this.on('transformend', (e) => {
            this.fixLinewidth()
            this.updateObjectSize();
            this.updateValueArc();
        });

        this.onStyle('main', 'line_color', () => {
            const { line_color } = this.style('main');
            this.backgroundArc.fill(line_color);
            this.backgroundArc.stroke(line_color);
        })
        this.onStyle('indicator', 'line_color', () => {
            const { line_color } = this.style('indicator');
            this.valueArc.fill(line_color);
            this.valueArc.stroke(line_color);
        })
        for (const prop of ['bg_color', 'bg_grad_color', 'bg_grad_dir', 'bg_main_stop', 'bg_grad_stop']) {
            this.onStyle('knob', prop, () => {
                this.knobNodes().forEach(knob => this.applyBackgroundStyle(knob, 'knob'));
            });
        }
        this.onStyle('knob', 'border_color', () => {
            this.knobNodes().forEach(knob => knob.stroke(this.style('knob').border_color));
        })
        this.on('value_color', (e) => { this.valueText.fill(this.value_color); })
        this.on('value_str', (e) => { this.valueText.text(processIconText(this.value_str)); });
        this.on('value_font', (e) => { this.valueText.fontSize(this.value_font); });
        this.on('value_font_resource', () => { this._applyValueFont(); });

        this.onStyle('main', 'line_width', () => { this.updateObjectSize(); });
        this.onStyle('indicator', 'line_width', () => { this.updateObjectSize(); });
        this.onStyle('main', 'line_rounded', () => {
            const cap = this.style('main').line_rounded ? 'round' : 'square';
            this.backgroundArc.lineRounded(cap);
        })
        this.onStyle('indicator', 'line_rounded', () => {
            const cap = this.style('indicator').line_rounded ? 'round' : 'square';
            this.valueArc.lineRounded(cap);
        })
        this.onStyle('main', 'radius', () => this.mainImagePreview?.updateBounds());

        // Shorthand + all four per-side overrides, one subscription helper.
        onPaddingStyle(this, 'main', () => { this.updateObjectSize(); });
        onPaddingStyle(this, 'indicator', () => { this.updateObjectSize(); });
        this.on('angle', (e) => {
            this.backgroundArc.angle(this.hasp_angle);
            this.end_angle = this.backgroundArc.endAngle();

            this.valueArc.angle(this.hasp_angle);
            this.updateValueArc();
        });
        this.on('rotation', (e) => {
            this.backgroundArc.haspRotation(this.hasp_rotation);
            this.end_angle = this.backgroundArc.endAngle();
            this.valueArc.haspRotation(this.hasp_rotation);
            this.updateValueArc();
        });

        this.on('type', (e) => {
            this.normalizeDataValues();
            this.createKnob();
            this.updateValueArc();
        });
        this.on('data_min', (e) => { this.updateValueArc(); });
        this.on('data_max', (e) => { this.updateValueArc(); });
        this.on('data_val', (e) => { this.updateValueArc(); });
        this.on('data_start_value', (e) => { this.updateValueArc(); });

        for (const prop of ['padding', 'pad_left', 'pad_right', 'pad_top', 'pad_bottom']) {
            this.onStyle('knob', prop, () => this.syncKnobBounds());
        }

        this.onStyle('knob', 'radius', () => {
            this.knobNodes().forEach(knob => knob.cornerRadius(Number(this.style('knob').radius ?? 0)));
            this.knobImagePreview?.updateBounds();
            this.startKnobImagePreview?.updateBounds();
        })

        this.onStyle('knob', 'border_width', () => {
            if (this.knob) this.knob.setStrokeWidth(Number(this.style('knob').border_width));
            if (this.startKnob) this.startKnob.setStrokeWidth(Number(this.style('knob').border_width));
        })

        this.on('adjustable', (e) => {
            this.adjustable = Boolean(this.adjustable);
            this.createKnob();
        });

        this.on('destroy', () => {
            this._valueFontBinding.dispose();
        });

        // lv_arc passes the outer object rectangle as clip_area to every arc,
        // indicator and knob draw call. Keep the Konva clip on an internal
        // content group so the widget's rect, selection and Transformer stay
        // outside the clip.
        this.contentGroup = createContentClipGroup(this);

        // LVGL 7 draws the BG pattern before either arc line. Keep this node
        // first in the clipped content group so the line renderers remain on
        // top of the image, just like lv_arc_design().
        this.mainImagePreview = attachImageSourcePreview(this, {
            part: 'main',
            getBounds: () => {
                const bounds = this.arcContentBounds();
                return {
                    imageBounds: bounds,
                    clipBounds: bounds,
                    clipRadius: this.styleProp('main', 'radius'),
                };
            },
        });
        this.contentGroup.add(this.mainImagePreview.imageNode);

        this.createBackgroundArc();
        this.createValueArc();
        this.createKnob(); // Ensure knobs are created after arcs

        // LVGL 7 draws the knob as a rect descriptor. Range has two rects,
        // both of which use the same KNOB style image source.
        this.knobImagePreview = this.createKnobImagePreview('val');
        this.contentGroup.add(this.knobImagePreview.imageNode);
        this.startKnobImagePreview = this.createKnobImagePreview('start');
        this.contentGroup.add(this.startKnobImagePreview.imageNode);
        this.syncKnobImageOrder();

        this.createText();
        this.setupRangeInteraction();
        this._applyValueFont();
        this.updateValueArc();
    }

    /**
     * The uploaded Font Resource `this.value_font_resource` resolves to for
     * canvas preview — see HaspGauge.js's identically-named helper for the
     * full semantics.
     */
    _valueFontResource() {
        return this.value_font_resource ? useFontStore().fonts.find(f => f.file === this.value_font_resource) : null;
    }

    /**
     * Resolve which font family the value text (this.valueText) should
     * render with. Falls back to 'Material Icons' (the pre-existing
     * hardcoded default) when no Font Resource is selected.
     */
    _applyValueFont() {
        this._valueFontBinding.set(this._valueFontResource(), 'Material Icons');
    }

    arcGeometry() {
        const mainPadding = resolvePadding(this.style('main'));
        const indicatorPadding = resolvePadding(this.style('indicator'));
        return lvglArcGeometry({
            width: this.rect.width(),
            height: this.rect.height(),
            pad_top: mainPadding.top,
            pad_right: mainPadding.right,
            pad_bottom: mainPadding.bottom,
            pad_left: mainPadding.left,
            indicatorPads: {
                pad_top: indicatorPadding.top,
                pad_right: indicatorPadding.right,
                pad_bottom: indicatorPadding.bottom,
                pad_left: indicatorPadding.left,
            },
        });
    }

    arcContentBounds() {
        const width = Math.max(0, Number(this.rect?.width?.() ?? this.width?.() ?? 0));
        const height = Math.max(0, Number(this.rect?.height?.() ?? this.height?.() ?? 0));
        return { x: 0, y: 0, width, height };
    }

    clipToArcContent(bounds) {
        const content = this.arcContentBounds();
        const source = bounds ?? content;
        const x = Number(source.x ?? 0);
        const y = Number(source.y ?? 0);
        const width = Math.max(0, Number(source.width ?? 0));
        const height = Math.max(0, Number(source.height ?? 0));
        const right = Math.min(content.x + content.width, x + width);
        const bottom = Math.min(content.y + content.height, y + height);
        const left = Math.max(content.x, x);
        const top = Math.max(content.y, y);
        return {
            x: left,
            y: top,
            width: Math.max(0, right - left),
            height: Math.max(0, bottom - top),
        };
    }

    createKnobImagePreview(which) {
        return attachImageSourcePreview(this, {
            part: 'knob',
            getBounds: () => {
                const node = which === 'start' ? this.startKnob : this.knob;
                const bounds = this.knobBounds(which);
                return {
                    imageBounds: bounds,
                    clipBounds: this.clipToArcContent(bounds),
                    clipRadius: this.styleProp('knob', 'radius'),
                    visible: Boolean(node?.visible()),
                };
            },
        });
    }

    syncKnobImageOrder() {
        const knobs = [this.knob, this.startKnob].filter(Boolean);
        if (knobs.length === 0) return;
        let nextZ = Math.max(...knobs.map(knob => knob.zIndex())) + 1;
        if (this.knobImagePreview) this.knobImagePreview.imageNode.zIndex(nextZ++);
        if (this.startKnobImagePreview) this.startKnobImagePreview.imageNode.zIndex(nextZ);
    }

    createBackgroundArc() {
        const geometry = this.arcGeometry();
        const main = this.style('main');
        const { line_width, line_color, line_rounded } = main;
        this.backgroundArc = new CustomArc({
            x: geometry.center.x,
            y: geometry.center.y,
            innerRadius: geometry.radius - line_width,
            outerRadius: geometry.radius,
            startAngle: this.start_angle,
            endAngle: this.end_angle,
            fill: line_color,
            stroke: line_color,
            haspRotation: this.hasp_rotation,
            // CustomArc expects a string ('round' or anything else) and falls
            // back via `config.lineRounded || "round"` — passing the boolean
            // `false` here previously always coerced to "round" regardless of
            // the resolved value, since `false` is falsy. Must pass a string.
            lineRounded: line_rounded ? 'round' : 'square',
        });
        this.contentGroup.add(this.backgroundArc);
    }

    createValueArc() {
        const geometry = this.arcGeometry();
        const { line_width: indicatorLineWidth } = this.style('indicator');
        const { line_color, line_rounded } = this.style('indicator');
        this.valueArc = new CustomArc({
            x: geometry.center.x,
            y: geometry.center.y,
            innerRadius: geometry.indicatorRadius - indicatorLineWidth,
            outerRadius: geometry.indicatorRadius,
            startAngle: this.start_angle,
            endAngle: this.start_angle,
            fill: line_color,
            stroke: line_color,
            haspRotation: this.hasp_rotation,
            // See createBackgroundArc — must be a string, not a boolean.
            lineRounded: line_rounded ? 'round' : 'square',
        });
        this.contentGroup.add(this.valueArc);
    }

    createText() {
        this.valueText = new Konva.Text({
            x: 0,
            y: 0,
            verticalAlign: 'middle',
            align: 'center',
            fontSize: this.value_font,
            fontFamily: 'Material Icons',
            draggable: true,
            listening: true,
            text: processIconText(this.value_str),
            fill: this.value_color,
            obj: 'value_text',
        });
        this.contentGroup.add(this.valueText);

        this.valueText.x((this.width() / 2) - (this.valueText.width() / 2) + (this.value_ofs_x || 0));
        this.valueText.y((this.height() / 2) - (this.valueText.height() / 2) + (this.value_ofs_y || 0));
    }

    knobNodes() {
        return [this.knob, this.startKnob].filter(Boolean);
    }

    isRange() {
        return Number(this.type) === ARC_RANGE_TYPE;
    }

    knobCount() {
        return arcKnobCount({ type: this.type, adjustable: this.adjustable });
    }

    createKnob() {
        this.knobNodes().forEach(knob => knob.destroy());
        this.knob = undefined;
        this.startKnob = undefined;

        if (this.knobCount() === 0) {
            this.knobImagePreview?.updateBounds();
            this.startKnobImagePreview?.updateBounds();
            return;
        }

        const knob = this.style('knob');
        const create = (which) => {
            const bounds = this.knobBounds(which);
            // Both Range knobs deliberately use this exact same renderer and
            // resolved style. Only the value/angle passed to knobBounds differs.
            const config = {
                x: bounds.x,
                y: bounds.y,
                width: bounds.width,
                height: bounds.height,
                fill: knob.bg_color,
                stroke: knob.border_color,
                borderWidth: Number(knob.border_width ?? 0),
                cornerRadius: Number(knob.radius ?? 0),
                draggable: false,
                listening: this.isRange(),
                obj: 'knob',
                visible: bounds.width > 0 && bounds.height > 0,
            };
            const node = which === 'val'
                ? (this.knob = new CustomRect(config))
                : new CustomRect(config);
            this.contentGroup.add(node);
            if (which === 'val') this.applyBackgroundStyle(this.knob, 'knob');
            else this.applyBackgroundStyle(node, 'knob');
            return node;
        };

        // Keep `knob` as the end/value knob for compatibility with existing
        // Arc code and tests. Range adds the start knob through the same path.
        this.knob = create('val');
        if (this.isRange()) this.startKnob = create('start');
        this.syncKnobImageOrder();
        this.knobImagePreview?.updateBounds();
        this.startKnobImagePreview?.updateBounds();
    }

    arcValueAngles() {
        return lvglArcValueAngles({
            type: this.type,
            min: this.min,
            max: this.max,
            value: this.val,
            startValue: this.start_value,
            startAngle: this.start_angle,
            endAngle: this.end_angle,
        });
    }

    knobAnchor(which = 'val') {
        const geometry = this.arcGeometry();
        const { line_width: indicatorLineWidth } = this.style('indicator');
        const angles = this.arcValueAngles();
        const angle = this.isRange() && which === 'start'
            ? angles.startKnobAngle
            : angles.knobAngle;
        return lvglArcKnobAnchor({
            center: geometry.center,
            radius: geometry.radius,
            indicatorLineWidth,
            angle: angle + Number(this.hasp_rotation || 0),
        });
    }

    calculateKnobPosition() {
        return this.knobAnchor();
    }

    knobBounds(which = 'val') {
        const { line_width: indicatorLineWidth } = this.style('indicator');
        const pads = resolveKnobPadding(this.style('knob'));
        return lvglArcKnobBounds({
            anchor: this.knobAnchor(which),
            indicatorLineWidth,
            pad_top: pads.top,
            pad_right: pads.right,
            pad_bottom: pads.bottom,
            pad_left: pads.left,
        });
    }

    syncKnobBounds() {
        const sync = (knob, which) => {
            if (!knob) return;
            const bounds = this.knobBounds(which);
            knob.position({ x: bounds.x, y: bounds.y });
            knob.size({ width: bounds.width, height: bounds.height });
            knob.visible(bounds.width > 0 && bounds.height > 0);
        };
        sync(this.knob, 'val');
        sync(this.startKnob, 'start');
        this.knobImagePreview?.updateBounds();
        this.startKnobImagePreview?.updateBounds();
    }

    updateObjectSize() {
        this.size(this.rect.size());

        // this.valueText.x();

        const geometry = this.arcGeometry();
        const { line_width } = this.style('main');
        const { line_width: indicatorLineWidth } = this.style('indicator');

        this.backgroundArc.x(geometry.center.x)
        this.backgroundArc.y(geometry.center.y)
        this.backgroundArc.innerRadius(geometry.radius - line_width)
        this.backgroundArc.outerRadius(geometry.radius)

        this.valueArc.x(this.backgroundArc.x())
        this.valueArc.y(this.backgroundArc.y())
        this.valueArc.innerRadius(geometry.indicatorRadius - indicatorLineWidth)
        this.valueArc.outerRadius(geometry.indicatorRadius)

        if (this.adjustable) this.syncKnobBounds();
        this.mainImagePreview?.updateBounds();
    }

    //valuearc is based on the type
    updateValueArc() {
        const angles = this.arcValueAngles();
        this.valueArc.startAngle(angles.start);
        this.valueArc.endAngle(angles.end);

        if (this.type === 0) this.valueArc.visible(this.val > this.min);
        else if (this.type === 1) this.valueArc.visible(this.val !== angles.rangeMidpoint);
        else if (this.type === 2) this.valueArc.visible(this.val < this.max);
        else if (this.type === ARC_RANGE_TYPE) this.valueArc.visible(this.val > this.start_value);

        if (this.adjustable) {
            this.syncKnobBounds();
        }
    }

    normalizeDataValues() {
        const normalized = normalizeRangeValues({
            min: this.min,
            max: this.max,
            value: this.val,
            startValue: this.start_value,
        });
        if (!normalized) return;
        this.val = normalized.value;
        this.start_value = normalized.startValue;
    }

    adjust(type, value, syncKnob = true) {
        if (['data_min', 'data_max', 'data_val', 'data_start_value'].includes(type)) {
            this.normalizeDataValues();
        }
        if (syncKnob) this.updateValueArc();
        super.adjust(type, value);
    }

    previewValueContext() {
        if (this.isRange()) return { start: this.start_value, val: this.val };
        return { val: this.val };
    }

    pointerLocalPosition() {
        const stage = this.getStage?.();
        const pointer = stage?.getPointerPosition?.();
        if (!pointer) return null;
        return this.getAbsoluteTransform().copy().invert().point(pointer);
    }

    setupRangeInteraction() {
        this._rangeInteractionNamespace = `.arcRange${++arcRangeInteractionId}`;
        this.contentGroup.on('mousedown touchstart', (event) => this.beginRangeDrag(event));
        this.on('destroy', () => this.endRangeDrag());
    }

    beginRangeDrag(event) {
        if (!this.isRange() || !this.adjustable) return;
        const point = this.pointerLocalPosition();
        if (!point) return;

        const start = this.knobAnchor('start');
        const end = this.knobAnchor('val');
        const distance = (anchor) => ((point.x - anchor.x) ** 2) + ((point.y - anchor.y) ** 2);
        this._activeRangeKnob = distance(start) <= distance(end) ? 'start' : 'val';

        const stage = this.getStage?.();
        if (stage) {
            const namespace = this._rangeInteractionNamespace;
            stage.off(`mousemove${namespace} touchmove${namespace} mouseup${namespace} touchend${namespace} touchcancel${namespace}`);
            stage.on(`mousemove${namespace} touchmove${namespace}`, () => this.updateRangeFromPointer());
            stage.on(`mouseup${namespace} touchend${namespace} touchcancel${namespace}`, () => this.endRangeDrag());
        }
        this.updateRangeFromPointer(point);
        event.cancelBubble = true;
    }

    endRangeDrag() {
        const stage = this.getStage?.();
        if (stage && this._rangeInteractionNamespace) {
            stage.off(`mousemove${this._rangeInteractionNamespace} touchmove${this._rangeInteractionNamespace} mouseup${this._rangeInteractionNamespace} touchend${this._rangeInteractionNamespace} touchcancel${this._rangeInteractionNamespace}`);
        }
        this._activeRangeKnob = undefined;
    }

    updateRangeFromPointer(point = this.pointerLocalPosition()) {
        if (!this.isRange() || !this.adjustable || !this._activeRangeKnob || !point) return false;
        const geometry = this.arcGeometry();
        const next = lvglArcValueFromPoint({
            center: geometry.center,
            point,
            min: this.min,
            max: this.max,
            startAngle: this.start_angle,
            endAngle: this.end_angle,
            rotation: this.hasp_rotation,
        });
        const start = Number(this.start_value ?? this.min);
        const value = Number(this.val);
        const nextStart = this._activeRangeKnob === 'start' ? Math.min(next, value) : start;
        const nextValue = this._activeRangeKnob === 'val' ? Math.max(next, start) : value;
        const mutate = () => {
            if (this.setPreviewProperty) {
                this.setPreviewProperty('start_value', nextStart);
                this.setPreviewProperty('val', nextValue);
            } else {
                this.start_value = nextStart;
                this.val = nextValue;
            }
            this.updateValueArc();
            this.adjust(undefined, undefined, false);
        };
        if (this.withPreviewMutation) this.withPreviewMutation(mutate);
        else mutate();
        executeActions(this, 'changed', this.previewValueContext());
        return true;
    }



    setNewSize(size) {
        super.setNewSize(size);
        this.fixLinewidth();
        this.updateObjectSize();
        this.updateValueArc();
    }

    fixLinewidth() {
        // Silent clamp (pokeStyle, not setStyle) — setNewSize already redraws
        // via updateObjectSize()/updateValueArc() right after; a full
        // refreshStyle here would be redundant and would spam an undo entry
        // on every resize that happens to clamp the line width.
        const { line_width } = this.style('main');
        const { left, right } = resolvePadding(this.style('main'));
        const maxLineWidth = (this.rect.width() - left - right) / 2;
        if (line_width > maxLineWidth) this.pokeStyle('main', 'line_width', maxLineWidth);
        const { line_width: indicatorLineWidth } = this.style('indicator');
        if (indicatorLineWidth > maxLineWidth) this.pokeStyle('indicator', 'line_width', maxLineWidth);
    }

    objectExport(page, objectData, hassConfig) {
        super.objectExport(page, objectData, hassConfig);

        if (this.adjustable === true) { objectData.adjustable = this.adjustable; }
        //to make type 2=reverse work we need to get the hasp rotation to 0
        //so we start_angle = rotation endangle = angle + rot
        objectData.start_angle = this.start_angle;
        objectData.end_angle = this.end_angle;
        if (this.hasp_rotation !== 0) objectData.rotation = this.hasp_rotation;

        // if (this.value_str !== '') {
        //     objectData.value_str = this.value_str;
        // }
        if (this.value_str !== '') {
            objectData.value_ofs_x = this.valueText.x() + (this.valueText.width() / 2) - (this.width() / 2);
            objectData.value_ofs_y = this.valueText.y() + (this.valueText.height() / 2) - (this.height() / 2);
        }

        if (this.hasProperty('type') && this.type !== undefined) {
            objectData.type = this.type;
        }
    }
}
